import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Save, Plus, X, Trash2, RotateCcw, Info, CalendarClock, Send } from "lucide-react";

const BUILT_IN = [
  { key: "booking_confirmation", label: "Booking confirmation", when: "Immediately on booking" },
  { key: "intake_form", label: "Intake form", when: "Auto — days-before is editable per show" },
  { key: "form_reminder", label: "Intake reminder", when: "Auto — only if form still incomplete" },
  { key: "preshow_checkin", label: "Pre-show check-in", when: "Auto — requires guest broadcast link" },
  { key: "day_of_reminder", label: "Day-of reminder", when: "Auto — 8 AM in your timezone on the recording day" },
  { key: "host_prep_reminder", label: "Host prep reminder (to you)", when: "Auto — nudges host, not guest" },
  { key: "post_thankyou", label: "Post-episode thank-you", when: "Manual send from Emails tab" },
];

const CADENCE_DEFAULTS = {
  intake_form: { days_before: 14 },
  form_reminder: { days_before: 7 },
  preshow_checkin: { days_before: 4 },
  host_prep_reminder: { days_before: 7, followup_days_before: 3 },
};

const DAY_OF_HINT = {
  day_of_reminder: "Fires automatically at 8 AM in your show timezone on the day of the recording. Set your timezone on Show Settings.",
};

const CADENCE_COPY = {
  intake_form: { title: "Send timing", hint: "Sent this many days BEFORE the recording. Only fires once the booking confirmation was logged." },
  form_reminder: { title: "Send timing", hint: "Nudge sent this many days BEFORE the recording — only if the guest still hasn't submitted the intake form." },
  preshow_checkin: { title: "Send timing", hint: "Sent this many days BEFORE the recording. Held until the guest has a broadcast link." },
  host_prep_reminder: { title: "Send timing (nudges you, not the guest)", hint: "You get this reminder at both windows, but only if the join link or promo copy is still missing." },
};

const TOKEN_GROUPS = [
  {
    source: "From the guest's Info tab",
    tokens: [
      { t: "guest_first_name", desc: "Guest's first name" },
      { t: "guest_full_name", desc: "Guest's full name" },
      { t: "episode_date", desc: "Episode date" },
      { t: "episode_time", desc: "Recording time (HH:MM) from the Info tab" },
      { t: "episode_time_short", desc: "Formatted time like \"2:30 PM ET\" (day-of subject line)" },
      { t: "recording_when", desc: "Long recording moment like \"Tuesday, Aug 26 at 2:30 PM Eastern\"" },
      { t: "broadcast_link", desc: "Join link — where the guest meets you to record (set per guest)" },
    ],
  },
  {
    source: "Auto-generated per guest",
    tokens: [
      { t: "intake_form_url", desc: "Guest's personal intake form link" },
      { t: "promo_kit_url", desc: "Guest's personal promo kit page" },
      { t: "show_prep_questions", desc: "Approved interview questions from Show Prep (empty until the host toggles \"Share with guest\")" },
      { t: "host_prep_outstanding", desc: "Auto list of what's still missing (host prep reminder only)" },
      { t: "guest_admin_url", desc: "Direct link to the guest record in the admin (host prep reminder)" },
    ],
  },
  {
    source: "From Show Settings",
    tokens: [
      { t: "show_name", desc: "Your show's name" },
      { t: "host_name", desc: "Host / show name" },
    ],
  },
  {
    source: "From the guest's Assets tab",
    tokens: [
      { t: "episode_link", desc: "Published episode link (after the episode goes live)" },
      { t: "storage_folder", desc: "Episode files — Dropbox folder with video, audio and clips" },
      { t: "clips_list", desc: "Bulleted list of the guest's clips" },
    ],
  },
];

export default function EmailTemplates() {
  const [show, setShow] = useState(null);
  const [active, setActive] = useState("booking_confirmation");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newTpl, setNewTpl] = useState({ key: "", label: "", subject: "", body: "" });
  const [preview, setPreview] = useState(null);
  const bodyRef = useRef(null);

  const reload = useCallback(async () => {
    const { data } = await api.get("/shows/me");
    setShow(data);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    api.get("/shows/me/cadence-preview").then(r => setPreview(r.data)).catch((e) => console.warn("Cadence preview failed:", e));
  }, [show?.email_cadence, show?.host_tz]);
  if (!show) return <div>Loading...</div>;

  const templates = show.email_templates || {};
  const cadence = show.email_cadence || {};
  const allKeys = [...BUILT_IN.map(b => b.key), ...Object.keys(templates).filter(k => !BUILT_IN.find(b => b.key === k))];
  const labelFor = (k) => BUILT_IN.find(b => b.key === k)?.label || (templates[k]?.label) || k;
  const meta = BUILT_IN.find(b => b.key === active);
  const tpl = templates[active] || { subject: "", body: "" };
  const isCustom = !BUILT_IN.find(b => b.key === active);
  const cadenceCfg = CADENCE_DEFAULTS[active];
  const currentCadence = { ...(cadenceCfg || {}), ...(cadence[active] || {}) };

  const update = (field, val) => {
    const next = { ...templates, [active]: { ...tpl, [field]: val } };
    setShow({ ...show, email_templates: next });
  };

  const updateCadence = (field, val) => {
    const n = Math.max(0, Math.min(60, parseInt(val, 10) || 0));
    setShow({
      ...show,
      email_cadence: { ...cadence, [active]: { ...currentCadence, [field]: n } },
    });
  };

  const insertToken = (t) => {
    const token = `{${t}}`;
    const ta = bodyRef.current;
    if (ta) {
      const start = ta.selectionStart ?? (tpl.body || "").length;
      const end = ta.selectionEnd ?? start;
      const body = tpl.body || "";
      update("body", body.slice(0, start) + token + body.slice(end));
      requestAnimationFrame(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + token.length; });
    } else {
      update("body", (tpl.body || "") + token);
    }
  };

  const sendTestSequence = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/email/test-sequence");
      setTestResult(data);
    } catch (e) {
      setTestResult({ error: e?.response?.data?.detail || "Something went wrong" });
    } finally { setTesting(false); }
  };

  const save = async () => {    setSaving(true);
    try {
      const { data } = await api.put("/shows/me", {
        email_templates: show.email_templates,
        email_cadence: show.email_cadence,
      });
      setShow(data);
      setStatus("Saved!");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Save failed — please try again.";
      setStatus(`Error: ${detail}`);
    } finally {
      setSaving(false);
      setTimeout(() => setStatus(""), 2500);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm(`Reset "${labelFor(active)}" to the built-in default? Your edits to this template will be replaced.`)) return;
    const { data } = await api.post(`/shows/me/templates/${active}/reset`);
    setShow(data); setStatus("Reset to default"); setTimeout(() => setStatus(""), 1500);
  };

  const addCustom = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/shows/me/templates", newTpl);
    setAddOpen(false); setNewTpl({ key: "", label: "", subject: "", body: "" });
    await reload();
    setActive(data.key);
  };

  const deleteCustom = async () => {
    if (!isCustom) return;
    if (!window.confirm(`Delete custom template "${labelFor(active)}"?`)) return;
    await api.delete(`/shows/me/templates/${active}`);
    await reload();
    setActive("booking_confirmation");
  };

  return (
    <div className="pgm-reveal">
      <div className="pgm-micro">◆ Five built-in touchpoints + your custom templates</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 className="pgm-display" style={{ fontSize: 44 }}>Email Templates</h1>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="pgm-btn pgm-btn-ghost" onClick={sendTestSequence} disabled={testing} data-testid="send-test-sequence-btn">
            <Send size={14} /> {testing ? "Sending…" : "Email me the whole sequence"}
          </button>
          <button className="pgm-btn" onClick={() => setAddOpen(true)} data-testid="add-template-btn"><Plus size={14} /> New template</button>
        </div>
      </div>
      {testResult && (
        <div className="pgm-callout" style={{ marginBottom: 14, background: testResult.error ? "var(--pgm-orange)" : undefined, color: testResult.error ? "var(--pgm-cream)" : undefined }} data-testid="test-sequence-result">
          {testResult.error
            ? `Test send failed: ${testResult.error}`
            : `Sent ${testResult.sent}/${testResult.total} test emails to ${testResult.to} — all 5 guest emails, both calendar invite versions (same event, so #2 should update #1 in place), the host alerts and the cancellation invite.`}
        </div>
      )}
      <p style={{ color: "var(--pgm-wine)", opacity: 0.75, marginBottom: 18 }}>
        Placeholders in {"{curly braces}"} are filled in automatically for each guest — click any placeholder below to insert it. Add as many custom templates as you need.
      </p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
        {allKeys.map(k => (
          <button key={k} className={`pgm-btn ${active === k ? "" : "pgm-btn-ghost"}`} style={{ padding: "8px 14px", fontSize: 11 }} onClick={() => setActive(k)} data-testid={`template-tab-${k}`}>
            {labelFor(k)}{!BUILT_IN.find(b => b.key === k) && " ★"}
          </button>
        ))}
      </div>

      <div className="pgm-callout" style={{ marginBottom: 14 }}>
        <strong>{labelFor(active)}</strong>{meta && ` · ${meta.when}`}{isCustom && " · Custom template"}
      </div>

      {DAY_OF_HINT[active] && (
        <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)", marginBottom: 14 }} data-testid={`cadence-card-${active}`}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <CalendarClock size={16} color="var(--pgm-magenta)" />
            <strong style={{ fontSize: 13 }}>Send timing</strong>
          </div>
          <p style={{ fontSize: 12, opacity: 0.75, margin: 0 }}>{DAY_OF_HINT[active]}</p>
          {preview?.guest && preview.previews?.day_of_reminder && (
            <div className="pgm-callout" style={{ marginTop: 10, borderLeftColor: "var(--pgm-olive)" }} data-testid={`preview-day_of_reminder`}>
              Would send <strong>{preview.previews.day_of_reminder.send_date}</strong> at <strong>{preview.previews.day_of_reminder.send_time_local}</strong> for <strong>{preview.guest.full_name}</strong>
              {preview.previews.day_of_reminder.recording_at && <> (recording at {preview.previews.day_of_reminder.recording_at})</>}
            </div>
          )}
        </div>
      )}

      {cadenceCfg && (
        <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-magenta)", marginBottom: 14 }} data-testid={`cadence-card-${active}`}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <CalendarClock size={16} color="var(--pgm-magenta)" />
            <strong style={{ fontSize: 13 }}>{CADENCE_COPY[active]?.title || "Send timing"}</strong>
          </div>
          <p style={{ fontSize: 12, opacity: 0.75, marginBottom: 12 }}>{CADENCE_COPY[active]?.hint}</p>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label className="pgm-label" htmlFor={`cadence-days-${active}`} style={{ margin: 0 }}>Send</label>
              <input
                id={`cadence-days-${active}`}
                type="number"
                min="0"
                max="60"
                step="1"
                className="pgm-input"
                style={{ width: 80, textAlign: "center" }}
                value={currentCadence.days_before ?? ""}
                onChange={(e) => updateCadence("days_before", e.target.value)}
                data-testid={`cadence-days-${active}`}
              />
              <span style={{ fontSize: 13, color: "var(--pgm-wine)" }}>days before the recording</span>
            </div>
            {"followup_days_before" in (cadenceCfg || {}) && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label className="pgm-label" htmlFor={`cadence-followup-${active}`} style={{ margin: 0 }}>Follow-up</label>
                <input
                  id={`cadence-followup-${active}`}
                  type="number"
                  min="0"
                  max="60"
                  step="1"
                  className="pgm-input"
                  style={{ width: 80, textAlign: "center" }}
                  value={currentCadence.followup_days_before ?? ""}
                  onChange={(e) => updateCadence("followup_days_before", e.target.value)}
                  data-testid={`cadence-followup-${active}`}
                />
                <span style={{ fontSize: 13, color: "var(--pgm-wine)" }}>days before the recording</span>
              </div>
            )}
          </div>
          {preview?.guest && preview.previews?.[active] && (
            <div className="pgm-callout" style={{ marginTop: 12, borderLeftColor: "var(--pgm-olive)" }} data-testid={`preview-${active}`}>
              Would send <strong>{preview.previews[active].send_date}</strong> for <strong>{preview.guest.full_name}</strong>
              {active === "host_prep_reminder" && preview.previews[active].followup_send_date && (
                <> · follow-up on <strong>{preview.previews[active].followup_send_date}</strong></>
              )}
            </div>
          )}
          {preview && !preview.guest && (
            <div className="pgm-callout" style={{ marginTop: 12, fontSize: 12, opacity: 0.75 }} data-testid={`preview-empty`}>
              {preview.message}
            </div>
          )}
          <div style={{ fontSize: 11.5, opacity: 0.65, marginTop: 10 }}>
            Scheduler checks every 30 minutes — timing is by date, not exact hour.
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, alignItems: "start" }}>
        <div className="pgm-card">
          <label className="pgm-label">Subject</label>
          <input className="pgm-input" value={tpl.subject || ""} onChange={(e) => update("subject", e.target.value)} data-testid="template-subject" />
          <label className="pgm-label" style={{ marginTop: 14 }}>Body</label>
          <textarea ref={bodyRef} className="pgm-textarea" style={{ minHeight: 320, fontFamily: "Arial" }} value={tpl.body || ""} onChange={(e) => update("body", e.target.value)} data-testid="template-body" />
        </div>

        <div className="pgm-card" style={{ borderLeftColor: "var(--pgm-olive)" }} data-testid="placeholder-guide">
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <Info size={14} color="var(--pgm-magenta)" />
            <strong style={{ fontSize: 13 }}>Placeholders — click to insert</strong>
          </div>
          <p style={{ fontSize: 11.5, opacity: 0.7, marginBottom: 12 }}>
            Each placeholder is replaced with real info when the email is previewed or sent for a guest.
          </p>
          {TOKEN_GROUPS.map(({ source, tokens }) => (
            <div key={source} style={{ marginBottom: 12 }}>
              <div className="pgm-micro" style={{ fontSize: 10, marginBottom: 6 }}>◆ {source.toUpperCase()}</div>
              {tokens.map(({ t, desc }) => (
                <button
                  key={t}
                  onClick={() => insertToken(t)}
                  title={`Insert {${t}}`}
                  style={{ display: "flex", alignItems: "baseline", gap: 8, width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", padding: "3px 0" }}
                  data-testid={`token-${t}`}
                >
                  <code style={{ background: "var(--pgm-cream)", padding: "2px 6px", fontSize: 11, color: "var(--pgm-magenta)", whiteSpace: "nowrap" }}>{"{" + t + "}"}</code>
                  <span style={{ fontSize: 11, opacity: 0.7 }}>{desc}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 16 }}>
        <div style={{ display: "flex", gap: 10 }}>
          {isCustom ? (
            <button className="pgm-btn pgm-btn-ghost" onClick={deleteCustom} style={{ borderColor: "var(--pgm-orange)", color: "var(--pgm-orange)" }} data-testid="template-delete"><Trash2 size={12} /> Delete template</button>
          ) : (
            <button className="pgm-btn pgm-btn-ghost" onClick={resetToDefault} data-testid="template-reset"><RotateCcw size={12} /> Reset to default</button>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {status && <span className="pgm-badge pgm-badge-olive">{status}</span>}
          <button className="pgm-btn" disabled={saving} onClick={save} data-testid="template-save"><Save size={14} /> {saving ? "Saving..." : "Save template"}</button>
        </div>
      </div>

      {addOpen && (
        <div onClick={() => setAddOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(78,29,94,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--pgm-paper)", border: "1.5px solid var(--pgm-wine)", borderLeft: "4px solid var(--pgm-magenta)", padding: 24, width: "100%", maxWidth: 520 }} className="pgm-reveal">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h2 className="pgm-display" style={{ fontSize: 24 }}>New custom template</h2>
              <button onClick={() => setAddOpen(false)} style={{ border: "none", background: "transparent", cursor: "pointer" }}><X size={20} /></button>
            </div>
            <form onSubmit={addCustom} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div><label className="pgm-label">Label (display name) *</label><input className="pgm-input" required value={newTpl.label} onChange={(e) => setNewTpl({ ...newTpl, label: e.target.value, key: newTpl.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") })} data-testid="new-template-label" placeholder="e.g. Sponsor follow-up" /></div>
              <div><label className="pgm-label">Internal key</label><input className="pgm-input" value={newTpl.key} onChange={(e) => setNewTpl({ ...newTpl, key: e.target.value })} placeholder="auto from label" data-testid="new-template-key" /></div>
              <div><label className="pgm-label">Subject (optional, can edit later)</label><input className="pgm-input" value={newTpl.subject} onChange={(e) => setNewTpl({ ...newTpl, subject: e.target.value })} data-testid="new-template-subject" /></div>
              <div><label className="pgm-label">Body (optional, can edit later)</label><textarea className="pgm-textarea" value={newTpl.body} onChange={(e) => setNewTpl({ ...newTpl, body: e.target.value })} data-testid="new-template-body" /></div>
              <button className="pgm-btn" type="submit" data-testid="new-template-submit">Create template</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
